import { AssetsProvider } from '@obvio/app'
import { serverClient } from '@obvio/server'

import { schema } from '@/api/schema'
import { HiddenHeading } from '@/components/HiddenHeading'
import { Layout } from '@/components/Layout'
import { PageSeo } from '@/components/PageSeo'
import { RoomCategoryPage } from '@/components/RoomCategoryPage'
import { REVALIDATE_SECONDS } from '@/constants/revalidate'
import { describeRichText, withSiteName } from '@/utils/seo'

import type { Schema } from '@obvio/app'
import type { DeepPick } from '@obvio/utils'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import type { ReactElement } from 'react'

const CATEGORY_SELECT = {
  id: true,
  title: true,
  description: true,
  seoTitle: true,
  seoDescription: true,
  image: true,
  slug: true,
  elements: {
    id: true,
    title: true,
    images: true,
    description: true,
    profitroomId: true,
    size: true,
    single: true,
    double: true,
    family: true,
    apartment: true,
    priceFrom: true,
    order: true,
  },
} as const

export type Data = DeepPick<
  Schema['ProfitroomRoomCategory'],
  typeof CATEGORY_SELECT
>

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isAdmin: true })

  const categories = await client.query('getProfitroomRoomCategories', {
    select: {
      slug: true,
    },
  })

  const paths = categories.map(({ slug }) => ({
    params: {
      slug,
    },
  }))

  return {
    paths,
    fallback: false,
  }
}

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string }>,
): Promise<GetStaticPropsResult<{ category: Data }>> {
  const client = await serverClient(schema, { ...ctx, isAdmin: true })

  const slug = ctx?.params?.slug

  if (!slug) {
    return { notFound: true }
  }

  const category = await client.query('getProfitroomRoomCategory', {
    args: { slug },
    select: CATEGORY_SELECT,
  })

  if (!category) {
    return { notFound: true }
  }

  return {
    ...client.ssrData({ category }),
    revalidate: REVALIDATE_SECONDS,
  }
}

type CategoryProps = {
  category: Data
}

function Category({ category }: CategoryProps): ReactElement {
  const heading = `Pokój ${category.title}`
  return (
    <Layout type="Główny">
      <PageSeo
        path={`pokoje/${category.slug}`}
        title={category.seoTitle || withSiteName(heading)}
        description={
          category.seoDescription || describeRichText(category.description)
        }
      />
      <HiddenHeading>{heading}</HiddenHeading>
      <AssetsProvider path={`/room-category/${category.id}`}>
        <RoomCategoryPage category={category} />
      </AssetsProvider>
    </Layout>
  )
}

export default Category
